/**
 * @fileoverview Hydration Utilities - Map API data to form values for case ingestion.
 *
 * Used when resuming an existing matter to populate form fields from the API response.
 * Also provides utilities for coverage entry mapping.
 */
import type { DetailedMatter } from '@/services/types/client-intake-types';
import { MatterType } from '@/services/types/client-intake-types';
import type {
  BenefitType,
  CoverageSchema,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

import { GATE_TASKS_DEFAULT } from '../schemas';
import type { CreateMatterFormValues } from '../types';

/**
 * Single coverage item in the case ingestion form.
 *
 * Simplified representation of a coverage record for form display.
 */
export interface CoverageEntry {
  existingId: string | null;
  policyType: PolicyType | null;
  carrierName: string;
  carrierId: string | null;
  divisionId: string | null;
  policyNumber: string;
  adjusterName: string;
  claimNumber: string;
  benefitType: BenefitType | null;
  policyLimit: string;
}

/**
 * Creates default values for the guided creation form.
 * Used when starting a new matter creation flow.
 */
export const createCreationFormDefaults = (): CreateMatterFormValues => ({
  firm: {
    id: '',
    name: '',
  },
  first_name: '',
  last_name: '',
  matter_type: MatterType.MOTOR_VEHICLE_ACCIDENT,
  gate_tasks_during_initial_review: GATE_TASKS_DEFAULT,
  representing_attorney: null,
  contact_client_directly: true,
  simplified_billing_code: null,
  case_manager_id: null,
  auto_task_assignment: null,
  date_of_loss: undefined,
  statute_of_limitations_date: undefined,
  incident_location: undefined, // Hydrated separately from incident API in context
  external_cms_url: null,
  external_drive_url: null,
  cms_email: null,
  case_id: null,
});

/**
 * Maps a DetailedMatter object to CreateMatterFormValues.
 * Used when hydrating the form from an existing matter (resume flow).
 */
export const mapMatterToCreationForm = (
  matter?: DetailedMatter | null,
): CreateMatterFormValues => {
  const defaults = createCreationFormDefaults();

  if (!matter) {
    return defaults;
  }

  return {
    ...defaults,
    // Basics fields - hydrate from client's first/last name
    first_name: matter.clients?.[0]?.first_name || '',
    last_name: matter.clients?.[0]?.last_name || '',
    matter_type: matter.matter_type || null,
    // Hydrate task gating from task_config - use false if not set (existing matters without the flag)
    gate_tasks_during_initial_review:
      matter.task_config?.gate_tasks_during_initial_review ?? GATE_TASKS_DEFAULT,
    firm: matter.firm
      ? {
          id: matter.firm.id,
          name: matter.firm.name,
        }
      : defaults.firm,

    // Details fields
    representing_attorney: matter.representing_attorney
      ? typeof matter.representing_attorney === 'string'
        ? matter.representing_attorney
        : matter.representing_attorney.id
      : null,
    contact_client_directly: matter.contact_client_directly ?? true,
    simplified_billing_code: matter.simplified_billing_code || null,
    case_manager_id: matter.case_manager?.id || null,
    date_of_loss: matter.date_of_loss || undefined,
    statute_of_limitations_date: matter.statute_of_limitations_date || undefined,
    incident_location: undefined, // Hydrated separately from incident API in context
    external_cms_url: matter.external_cms_url || null,
    external_drive_url: matter.external_drive_url || null,
    cms_email: matter.cms_email || null,
    case_id: matter.case_id || null,
  };
};

/**
 * Creates an empty coverage entry for the form.
 * Used when adding a new coverage to any category.
 */
export const createEmptyCoverageEntry = (): CoverageEntry => ({
  existingId: null,
  policyType: null,
  carrierName: '',
  carrierId: null,
  divisionId: null,
  policyNumber: '',
  adjusterName: '',
  claimNumber: '',
  benefitType: null,
  policyLimit: '',
});

/**
 * Maps a CoverageSchema to a CoverageEntry for form hydration.
 * Extracts relevant fields from the full coverage object.
 */
export const mapCoverageToEntry = (
  coverage: CoverageSchema,
): CoverageEntry => {
  // Extract policy number from identifiers
  const policyIdentifier = coverage.policy_identifiers?.find(
    (pi) => pi.identifier_type === ('POLICY_NUMBER' as IdentifierType),
  );

  // Extract first benefit for simplified form
  const benefit = coverage.benefits?.[0];

  // Format policy limit as currency string
  const policyLimit = benefit?.limitations?.limit
    ? `$${benefit.limitations.limit.toLocaleString()}`
    : '';

  return {
    existingId: coverage.id,
    policyType: coverage.policy_type || null,
    carrierName: coverage.carrier?.name || '',
    carrierId: coverage.carrier_v2_id || null,
    divisionId: coverage.carrier_division_id || null,
    policyNumber: policyIdentifier?.identifier_value || '',
    adjusterName: coverage.adjusters?.[0]?.name || '',
    claimNumber: coverage.claims?.[0]?.claim_identifier || '',
    benefitType: benefit?.benefit_type || null,
    policyLimit,
  };
};
